import {
  AdjustResult,
  AloftPrice,
  PairedSalesAnalysisFeature,
  StudiesPair,
} from '@aloft/toolkit-shared';

import { getTotalAdjustedValuePaired } from '../../adjustPairedSalesStandalone/getTotalAdjustedValuePaired.js';
import { PairedSalesSqFtFeature, isPairedSalesSqFtFeature } from '../types.ts';

type Params = {
  feature: PairedSalesAnalysisFeature;
  pair: StudiesPair;
};

type Return = {
  result: AdjustResult;
  formulaString: string;
};

const fallback: Return = {
  result: {
    t: 'absolute',
    value: AloftPrice.zero,
  },
  formulaString: '',
};

export function makeSqFtFeatureAdjustResultAndFormulaString({ feature, pair }: Params): Return {
  if (!isPairedSalesSqFtFeature(feature)) {
    return fallback;
  }

  const p1Price = pair.p1.salePrice ?? AloftPrice.zero;
  const p2Price =
    getTotalAdjustedValuePaired(pair.p2.salePrice ?? AloftPrice.zero, pair.adjustments) ??
    AloftPrice.zero;

  const p1SqFt = pair.p1[feature as PairedSalesSqFtFeature];
  const p2SqFt = pair.p2[feature as PairedSalesSqFtFeature];

  if (!p1SqFt || !isFinite(p1SqFt?.unitAmount) || !p2SqFt || !isFinite(p2SqFt.unitAmount)) {
    return fallback;
  }

  const dividend = p1Price.sub(p2Price);
  const divisor = p1SqFt.sub(p2SqFt).unitAmount;

  if (divisor === 0) {
    return fallback;
  }

  const result: AdjustResult = {
    t: 'absolute',
    value: dividend.div(divisor),
  };

  const dividendStr = `${p1Price.toString(true)} - ${p2Price.toString(true)}`;
  const divisorStr = `${p1SqFt.toSqFtValueStringRounded()} sf - ${p2SqFt.toSqFtValueStringRounded()} sf`;
  const resultStr = `${dividend.div(divisor).toString(true)}`;
  const formulaString = `(${dividendStr}) / (${divisorStr}) = ${resultStr}`;

  return {
    result,
    formulaString,
  };
}
